import React, {useCallback, useEffect, useState} from 'react';
import _ from 'lodash';

import {connect, resourceActions} from 'redux/index.js';

import {
  pointerWithin,
  DndContext,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {snapCenterToCursor, restrictToWindowEdges} from '@dnd-kit/modifiers';

import DroppableSection from '../../components/drag-drop/droppable-section';
import {arrayMove, PointerSensor, moveBetweenContainers, removeAtIndex} from '../../helpers/dnd-helpers';
import {loadMedia} from 'helpers/loaders';
import {useOutletContext} from 'react-router-dom';
import Item from 'components/drag-drop/item';
import {defaultRorschachTestData, ModeOptions} from './rorschach-page';
import K from 'k';
import useDetectPrint from 'hooks/useDetectPrint';

import '../../index.scss';

const DirectionBoardView = ({media, trackMedia, updateProject, notesAreVisible, imageSizeKey, mode/*, shouldPrint, setShouldPrint*/}) => {
  const [project] = useOutletContext();
  //HINT convert to Map to leverage get/set
  const items = new Map(Object.entries(_.defaults(project?.rorschachTestData, defaultRorschachTestData) || defaultRorschachTestData));

  const [draggingId, setDraggingId] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const isPrinting = useDetectPrint();

  const projectId = project?.id;

  useEffect(() => {
    if (!_.isEmpty(project?.rorschachTestData) && !hasLoaded) {
      requestMediaForRorschachTestData({rorschachTestData: project.rorschachTestData});
    }
  }, [project?.rorschachTestData]);

  useEffect(() => {
    if (projectId) {
      requestMediaForRorschachTestData({rorschachTestData: project.rorschachTestData, reset: true});
    }
  }, [projectId, mode]);

  const requestMediaForRorschachTestData = useCallback(async ({rorschachTestData, reset = false}) => {
    const items = _.cloneDeep(rorschachTestData);

    const mediaIds = _.flatMap(_.keys(items), sectionKey => {
      return _.map(items[sectionKey], 'id');
    });

    const resourceActions = {trackMedia};
    const where = {id: mediaIds};

    if (mode === ModeOptions.DIRECTION) where.inRorschachSet = 1;
    else if (mode === ModeOptions.PROPOSAL) where.inProposalSet = 1;

    await loadMedia({resourceActions, where, pageCount: mediaIds.length, projectId, reset});

    setHasLoaded(true);
  });

  const styles = {
    collapsibleHeader: {marginBottom: K.spacing},
    dndWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignContent: 'center',
    },
    projectTitle: {...K.fonts.projectTitle, padding: K.spacing * 2.5},
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      margin: `${isPrinting ? '0' : '3rem'} 6rem 0 6rem`,
    },
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
  );

  const handleUpdateRorschachTestData = async (data, hitApi = true) => {
    const rorschachTestData = Object.fromEntries(data); //HINT convert Map to Object before saving to db

    await updateProject({id: projectId, hitApi, props: {rorschachTestData}});
  };

  const handleUpdateItems = ({item, fromKey, toKey, fromIndex, toIndex}, hitApi = true) => {
    const updatedItems = moveBetweenContainers({map: items, fromKey, toKey, fromIndex, toIndex, item});

    handleUpdateRorschachTestData(updatedItems, hitApi);
  };

  const handleDragStart = (event) => {
    const {active} = event;

    setDraggingId(active.id);
  };

  const handleDragOver = ({over, active}) => {
    const overId = over?.id;
    if (!overId) {
      return;
    }

    const fromKey = active.data.current.sortable?.containerId;
    const toKey = over.data.current?.sortable.containerId;

    if (!_.isString(toKey)) {
      return;
    }

    if (!_.isString(fromKey)) {
      return;
    }

    if (active?.id && fromKey !== toKey) {
      const fromArray = items.get(fromKey);
      const fromIndex = active.data.current.sortable.index;
      const toIndex = over.data.current?.sortable.index || 0;
      const item = fromArray[fromIndex];

      handleUpdateItems({item, fromKey, toKey, fromIndex, toIndex}, false); //HINT don't hit api while dragging over
    }
  };

  const handleDragEnd = async ({active, over}) => {
    setDraggingId(null);

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      const fromKey = active.data.current.sortable.containerId;
      const toKey = over.data.current?.sortable.containerId || over.id;
      const fromIndex = active.data.current.sortable.index;
      const toIndex = over.data.current?.sortable.index || 0;

      let updatedItems = new Map(items);

      const fromArray = updatedItems.get(fromKey);
      const item = fromArray[fromIndex];

      if (fromKey === toKey) {
        updatedItems.set(toKey, arrayMove({array: items.get(toKey), fromIndex, toIndex}));
      }
      else {
        updatedItems = moveBetweenContainers({map: items, fromKey, fromIndex, toKey, toIndex, item});
      }

      await handleUpdateRorschachTestData(updatedItems);
    }
  };

  const handleRemoveMedium = (fromKey) => async ({id}) => {
    const updatedItems = new Map(items);
    const fromArray = updatedItems.get(fromKey);
    const index = _.findIndex(fromArray, {id});

    if (index !== -1) {
      updatedItems.set(fromKey, removeAtIndex({array: fromArray, index}));

      await handleUpdateRorschachTestData(updatedItems);
    }
  };

  const updateNotes = (sectionKey) => async ({itemId, value}) => {
    const updatedItems = new Map(items);
    const updatedSection = updatedItems.get(sectionKey);
    const index = _.findIndex(updatedSection, {id: itemId});

    if (index !== -1) {
      const existingItem = updatedSection[index];
      const updatedItem = {
        ...existingItem,
        notes: value,
      };
      updatedSection.splice(index, 1, updatedItem);

      updatedItems.set(sectionKey, updatedSection);

      await handleUpdateRorschachTestData(updatedItems);
    }
  };

  const draggingUrl = _.find(media, medium => medium.id === draggingId)?.url;

  //HINT hides 'toOrganize' section if empty
  const orderedSectionIds = _.filter(['toOrganize', 'overview', 'importantThemes', 'specificDetails', 'proposalCoverPage', 'proposalImageGallery',], sectionKey => {
    var shouldShowSection = true;
    var sectionIsEmpty = items.get(sectionKey).length < 1;

    if (isPrinting && sectionIsEmpty) shouldShowSection = false;

    if (sectionKey === 'toOrganize' && sectionIsEmpty) shouldShowSection = false;

    if (_.includes(['proposalCoverPage', 'proposalImageGallery'], sectionKey) && mode === ModeOptions.DIRECTION) {
      shouldShowSection = false;
    }
    else if (_.includes(['overview', 'importantThemes', 'specificDetails'], sectionKey) && mode === ModeOptions.PROPOSAL) {
      shouldShowSection = false;
    }

    return shouldShowSection;
  });

  const itemSize = imageSizeKey === 'large' ? {width: 250, height: 250} : {width: '1.5in', height: '1.5in'};

  return (
    <div style={styles.wrapper}>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div style={styles.dndWrapper}>
          {_.map(orderedSectionIds, sectionKey => {
            const sectionItems = items.get(sectionKey) || [];
            const mediaIds = _.map(media, (value, key) => parseInt(key));

            const filteredSectionItems = _.filter(sectionItems, item => _.includes(mediaIds, item.id));

            return (
              <DroppableSection
                {...{draggingId, notesAreVisible, hasLoaded, itemSize, isPrinting}}
                id={sectionKey}
                items={filteredSectionItems}
                key={`droppable-section-${sectionKey}`}
                onRemove={handleRemoveMedium(sectionKey)}
                onUpdateNotes={updateNotes(sectionKey)}
              />
            );
          })}
          <DragOverlay modifiers={[snapCenterToCursor, restrictToWindowEdges]}>
            {draggingId && <Item id={draggingId} url={draggingUrl} isDragOverlay size={itemSize}/>}
          </DragOverlay>
        </div>
      </DndContext>
    </div>
  );
};

export default connect({
  mapState: state => {
    return {
      media: state.resources.media.byId
    };
  },
  mapDispatch: {
    ..._.pick(resourceActions.projects, ['trackProjects', 'updateProject']),
    ..._.pick(resourceActions.media, ['trackMedia']),
  }
})(DirectionBoardView);
